import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Anchor, ArrowLeft, CheckCircle2, Download, FileText, RotateCcw, Save } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { ouvrirPdf } from '../components/reglement-interieur/SignerReglementDialog';
import { EngagementsLivretBase } from '../components/livret-base/EngagementsLivretBase';
import { ChoixPhotosDialog } from '../components/livret-base/ChoixPhotosDialog';
import { PhotosPartie } from '../components/livret-base/PhotosPartie';

const Bouton = ({ onClick, Icone, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/10"><Icone className="h-4 w-4" /> {children}</button>
);

const Section = ({ i, s, texte, defaut, onChange, edition, photos }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`livret-base-section-${s.cle}`}>
    <div className="mb-2 flex items-center gap-3">
      <span className="text-xl font-black text-[#8DC63F]">{String(i + 1).padStart(2, '0')}</span>
      <h3 className="flex-1 font-semibold text-[#0F172A]">{s.titre}</h3>
      {edition && texte !== defaut && (
        <button type="button" onClick={() => onChange(defaut)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0F172A]" data-testid={`livret-base-retablir-${s.cle}`}>
          <RotateCcw className="h-3.5 w-3.5" /> Texte proposé
        </button>
      )}
    </div>
    {photos}
    <Textarea value={texte} onChange={(e) => onChange(e.target.value)} readOnly={!edition} rows={Math.min(18, texte.split('\n').length + 2)}
      className="font-mono text-[13px] leading-relaxed" data-testid={`livret-base-texte-${s.cle}`} />
  </div>
);

export default function LivretBase({ user }) {
  const [data, setData] = useState(null);
  const [textes, setTextes] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [photos, setPhotos] = useState({});
  const [choix, setChoix] = useState(null);
  const charger = useCallback(() => {
    axios.get('/livret-base/config').then(({ data: d }) => { setData(d); setTextes(d.textes); setPhotos(d.photos || {}); }).catch(() => setData({ erreur: true }));
  }, []);
  useEffect(() => { charger(); }, [charger]);

  if (!data) return <div className="p-8 text-slate-500">Chargement…</div>;
  if (data.erreur) return <div className="p-8 text-red-600" data-testid="livret-base-erreur">Accès non autorisé ou erreur de chargement.</div>;
  const edition = ['centre', 'direction'].includes(user?.role);

  const enregistrer = async (publie) => {
    setEnvoi(true);
    try {
      const { data: d } = await axios.put('/livret-base/config', { textes, publie, photos });
      setData(d); setTextes(d.textes); setPhotos(d.photos || {});
      toast.success(publie ? 'Livret enregistré et publié' : 'Livret enregistré');
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); }
    finally { setEnvoi(false); }
  };

  return (
    <div className="space-y-6" data-testid="livret-base-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-[#0F172A]"><ArrowLeft className="h-4 w-4" /> Tableau de bord</Link>
      <header className="rounded-2xl bg-[#0F172A] p-6 text-white shadow-sm sm:p-8">
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-xl bg-[#8DC63F]/20 p-3"><Anchor className="h-7 w-7 text-[#8DC63F]" /></div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Administratif CFA</p>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Carnet d'intégration</h1>
          </div>
          <Bouton onClick={() => ouvrirPdf('/livret-base/pdf', 'Carnet_integration_FISL.pdf')} Icone={FileText} testId="livret-base-voir-pdf">Voir le PDF</Bouton>
          <Bouton onClick={() => ouvrirPdf('/livret-base/pdf', 'Carnet_integration_FISL.pdf', 'download')} Icone={Download} testId="livret-base-telecharger-pdf">Télécharger</Bouton>
        </div>
        <p className="mt-3 text-sm text-white/70">Règles de vie de bon sens sur la base de pratique (ouverte aux clients, dans un port passant) et en entreprise de stage. Une fois publié, il est consultable
          par les stagiaires, les tuteurs et les formateurs ; chaque stagiaire valide son engagement daté (preuve Qualiopi, indicateur 9). Il peut aussi être joint aux e-mails.</p>
        <span className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${data.publie ? 'bg-emerald-500/90' : 'bg-white/15'}`} data-testid="livret-base-statut-publie">
          <CheckCircle2 className="h-4 w-4" /> {data.publie ? 'Publié' : 'Non publié'}
        </span>
      </header>
      <EngagementsLivretBase />
      {edition && (
        <p className="text-xs text-slate-500" data-testid="livret-base-aide">Mise en forme : « - » en début de ligne = puce, « x » = interdit (encadré rouge), « * » = vignette d'impact, « &gt; » = encadré à retenir, **texte** = gras, ligne vide = nouveau paragraphe.</p>
      )}
      <div className="grid gap-4">
        {edition && (
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="livret-base-couverture">
            <h3 className="mb-2 font-semibold text-[#0F172A]">Photo de couverture</h3>
            <PhotosPartie ids={photos.couverture} nbDefaut={1} testId="livret-base-photos-couverture"
              onChoisir={() => setChoix({ cle: 'couverture', titre: 'Couverture', max: 1 })} />
          </div>
        )}
        {data.sections.map((s, i) => (
          <Section key={s.cle} i={i} s={s} texte={textes[s.cle] || ''} defaut={data.defauts?.[s.cle] || ''} edition={edition}
            onChange={(v) => setTextes({ ...textes, [s.cle]: v })}
            photos={edition && <PhotosPartie ids={photos[s.cle]} nbDefaut={s.nb_defaut} testId={`livret-base-photos-${s.cle}`}
              onChoisir={() => setChoix({ cle: s.cle, titre: s.titre, max: data.max_photos })} />} />
        ))}
      </div>
      <ChoixPhotosDialog open={!!choix} titre={choix?.titre} max={choix?.max || 1} choisies={choix ? photos[choix.cle] : null}
        onClose={() => setChoix(null)}
        onValider={(ids) => {
          const suite = { ...photos };
          if (ids === null) delete suite[choix.cle]; else suite[choix.cle] = ids;
          setPhotos(suite); setChoix(null);
          toast.info('Pensez à enregistrer pour mettre à jour le PDF');
        }} />
      {edition && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
          <label className="flex flex-1 items-center gap-3 text-sm font-medium text-slate-700">
            <Switch checked={data.publie} disabled={envoi} onCheckedChange={(v) => enregistrer(v)} data-testid="livret-base-publier" />
            Publié (stagiaires, tuteurs et formateurs)
          </label>
          <button type="button" onClick={() => enregistrer(data.publie)} disabled={envoi} data-testid="livret-base-enregistrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#1E293B] disabled:opacity-50">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
        </div>
      )}
    </div>
  );
}
